'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import ClockIcon from '@/components/icons/ClockIcon';
import MarkerPinIcon from '@/components/icons/MarkerPinIcon';
import { Button } from '@/components';
import type { EventsTab, ListedEvent } from '@/lib/events-listings';
import { getEventsByTab } from '@/lib/events-listings';
import { useCartOptional } from '@/components/contexts/CartContext';
import { cmsImageUnoptimized, resolveCmsImageSrc } from '@/lib/cms-image-url';
import MediaPlaceholder from '@/components/ui/MediaPlaceholder';

const READ_MORE_FOCUS =
  '';

function splitPartnerLineToTwoRows(line: string): [string, string] {
  const words = line.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return ['', ''];
  if (words.length === 1) return [words[0], ''];
  if (words.length === 2) return [words[0], words[1]];
  return [words.slice(0, -1).join(' '), words[words.length - 1] ?? ''];
}

function EventCard({ event }: { event: ListedEvent }) {
  const [partnerRow1, partnerRow2] = splitPartnerLineToTwoRows(event.partnerLine);
  const heroSrc = resolveCmsImageSrc(event.heroImage || null, '');
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border-2 border-blue-100 bg-blue-100 p-2 shadow-[0_4px_24px_rgba(23,22,90,0.08)] sm:rounded-[1.25rem] sm:p-2.5">
      <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-xl">
        {heroSrc ? (
          <Image
            src={heroSrc}
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            unoptimized={cmsImageUnoptimized(heroSrc)}
          />
        ) : (
          <MediaPlaceholder />
        )}
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/65"
          aria-hidden
        />

        <div className="absolute inset-0 z-[2] isolate flex flex-col items-center justify-center gap-2 px-4 text-center">
          <div className="relative z-[30]">
            <Image
              src="/media/brand/logo.webp"
              alt="Dank"
              width={280}
              height={144}
              className="h-16 w-auto max-w-[min(300px,78vw)] object-contain drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)] sm:h-[5.25rem] md:h-24"
            />
          </div>
          <span className="relative z-[20] text-2xl font-bold leading-none text-constant-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] sm:text-3xl">
            ×
          </span>
          <div className="relative z-10 flex flex-col gap-1">
            <span className="block text-xl font-extrabold uppercase leading-tight tracking-[0.2em] text-constant-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)] sm:tracking-[0.22em] sm:text-2xl md:tracking-[0.24em]">
              {partnerRow1}
            </span>
            {partnerRow2 ? (
              <span className="block text-xl font-extrabold uppercase leading-tight tracking-[0.2em] text-constant-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)] sm:tracking-[0.22em] sm:text-2xl md:tracking-[0.24em]">
                {partnerRow2}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-1 flex-col rounded-xl border border-neutral-10 bg-constant-white p-4 sm:mt-2.5 sm:p-5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm font-medium text-text-secondary">
          <span className="inline-flex items-center gap-1.5">
            <ClockIcon size={16} color="currentColor" className="shrink-0 opacity-90" aria-hidden />
            {event.dateLabel}
          </span>
          <span className="select-none text-neutral-15" aria-hidden>
            |
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MarkerPinIcon size={16} color="currentColor" className="shrink-0 opacity-90" aria-hidden />
            {event.location}
          </span>
        </div>

        <h3 className="mt-4 text-body-20 font-bold leading-snug text-text-primary">{event.title}</h3>

        <p className="mt-2 line-clamp-2 flex-1 text-body-16 font-normal leading-relaxed text-text-secondary">
          {event.description}
        </p>

        <Button asChild variant="tertiary" size="lg" className="w-full rounded-xl mt-4" aria-describedby={READ_MORE_FOCUS}>
          <Link href={`/events/${event.slug}`}>
            Read more
          </Link>
        </Button>
      </div>
    </article>
  );
}

type UpcomingEventsSectionProps = {
  /** When viewing an event detail page, hide that slug from the grid. */
  excludeEventSlug?: string | null;
  /** Extra classes on the outer `<section>` (e.g. horizontal padding when the parent has none). */
  sectionClassName?: string;
};

export default function UpcomingEventsSection({
  excludeEventSlug = null,
  sectionClassName = '',
}: UpcomingEventsSectionProps = {}) {
  /**
   * Scoped to the shopper's store, matching the locations set on each event in
   * the admin panel. An event with no locations attached is shown everywhere.
   *
   * There is no toggle for this. It was briefly a checkbox, which put a control
   * on the page for something nobody asked to change: if you are shopping
   * Okotoks, the Ogden brand night is not your evening. The trade is that a
   * store with nothing booked shows an empty tab, so the empty copy points at
   * the store rather than claiming there is nothing on anywhere.
   */
  const cart = useCartOptional();
  const storeCovaLocationId = cart?.state.storeCovaLocationId ?? null;
  const [tab, setTab] = useState<EventsTab>('upcoming');
  const [events, setEvents] = useState<ListedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const data = await getEventsByTab(tab, storeCovaLocationId);
        if (!cancelled) setEvents(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tab, storeCovaLocationId]);

  const visibleEvents =
    excludeEventSlug ? events.filter((e) => e.slug !== excludeEventSlug) : events;

  return (
    <section
      className={['relative', sectionClassName].filter(Boolean).join(' ')}
      aria-labelledby="events-list-heading"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <h2 id="events-list-heading" className="text-h3-mobile font-bold leading-tight text-blue-100 sm:text-h3-desktop">
          {tab === 'upcoming' ? 'Upcoming Events' : 'Events Archive'}
        </h2>

        <div
          className="inline-flex shrink-0 rounded-xl border border-neutral-15 bg-neutral-5 p-1 gap-1"
          role="tablist"
          aria-label="Event list view"
        >
          <Button
            variant={tab === 'upcoming' ? 'primary' : 'bordered'}
            size="lg"
            role="tab"
            aria-selected={tab === 'upcoming'}
            id="tab-upcoming-events"
            aria-controls="events-cards-panel"
            onClick={() => setTab('upcoming')}
            className="rounded-xl px-4 py-2 max-sm:w-full "
          >
            Upcoming Events
          </Button>
          <Button
            variant={tab === 'archive' ? 'primary' : 'bordered'}
            size="lg"
            role="tab"
            aria-selected={tab === 'archive'}
            id="tab-events-archive"
            aria-controls="events-cards-panel"
            onClick={() => setTab('archive')}
            className="rounded-xl px-4 py-2 max-sm:w-full"
          >
            Events Archive
          </Button>
        </div>
      </div>


      <div
        id="events-cards-panel"
        role="tabpanel"
        aria-labelledby={tab === 'upcoming' ? 'tab-upcoming-events' : 'tab-events-archive'}
        className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {loading ? (
          <div className="col-span-full py-12 text-center text-text-secondary">Loading…</div>
        ) : visibleEvents.length === 0 ? (
          <div className="col-span-full py-12 text-center text-text-secondary">
            {tab === 'upcoming'
              ? storeCovaLocationId
                ? 'Nothing booked at your store right now. Switch stores in the header to see what is on elsewhere.'
                : 'No events on the calendar right now. Check back soon.'
              : storeCovaLocationId
              ? 'Nothing in your store\u2019s archive yet.'
              : 'Nothing in the archive yet.'}
          </div>
        ) : (
          visibleEvents.map((event) => <EventCard key={event.id} event={event} />)
        )}
      </div>
    </section>
  );
}
